import api from 'api'
import { Button, DropdownItem, HFlow, Icon, Tooltip } from 'bold-ui'
import { useAlert } from 'components/alert'
import { useSession } from 'components/auth/useSession'
import { DropdownButton } from 'components/dropdown'
import { confirm } from 'components/modals/confirm'
import { TipoAtestadoEnum } from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { reportFormatDate, useHandleImpressao } from 'hooks/impressao/useHandleImpressao'
import { useState } from 'react'
import { isSoapEditableItem } from 'view/atendimentos/atendimento-individual/util-atendimentoIndividual'

import AtestadoModel, { CidadaoAtestado } from '../components/model-atestado'
import AtestadoEmBrancoFormModal from '../em-branco/AtestadoEmBrancoFormModal'
import LicencaMaternidadeFormModal from '../licenca-maternidade/LicencaMaternidadeFormModal'
import { AtestadoImpressaoInput, AtestadoImpressaoWrapperModel, AtestadoRecenteImpressaoInput } from '../model-atestado'
import AtestadoPadraoFormModal from '../padrao/AtestadoPadraoFormModal'
import SolicitacaoFraldasGeriatricasFormModal from '../solicitacao-fraldas-geriatricas/SolicitacaoFraldasGeriatricasFormModal'
import { getTooltipTextMaisOpcoesAtestado, grupoCboImprimirAtestado } from '../utils-atestado'
import { RegistroLista } from './AtestadosTable'
import AtestadosVisualizacaoModal from './AtestadosVisualizacaoModal'

export interface AtestadoTableButtonsProps {
  registro: RegistroLista
  cidadao: CidadaoAtestado
  atestadoRecente?: AtestadoModel
  atendimentoId?: ID
  prontuarioId: ID
  update(index: number, value: AtestadoModel): void
  remove(index: number): void
  readOnly: boolean
  dataTestId?: string
}

export default function AtestadoTableButtons(props: Readonly<AtestadoTableButtonsProps>) {
  const {
    registro,
    cidadao,
    atestadoRecente,
    update,
    remove,
    atendimentoId,
    prontuarioId,
    readOnly,
    dataTestId,
  } = props

  const { hasCboAuth } = useSession({ fetchPolicy: 'cache-only' })
  const alert = useAlert()

  const hasPermissionImprimirAtestado = hasCboAuth(grupoCboImprimirAtestado(registro.tipo))
  const isAssinadoDigitalmente = registro.assinadoDigitalmente

  const [isModalVisualizacaoOpen, setModalVisualizacaoOpen] = useState(false)
  const [isEditModalOpen, setEditModalOpen] = useState(false)
  const [isPrintLoading, setIsPrintLoading] = useState(false)

  const isEditable = isSoapEditableItem(atestadoRecente)
  const tooltipTextMaisOpcoes = getTooltipTextMaisOpcoesAtestado(isAssinadoDigitalmente, registro.recente, isEditable)

  const { analytics } = useFirebase()

  const { handleImpressaoPath, handleImpressaoError } = useHandleImpressao()

  const handleExcluir = () => {
    const isLicencaMaternidade = TipoAtestadoEnum.LICENCA_MATERNIDADE === atestadoRecente.tipo
    const titleConfirm = isLicencaMaternidade ? 'Deseja excluir a licença-maternidade?' : 'Deseja excluir o atestado?'
    const alertSuccess = isLicencaMaternidade
      ? 'Licença-maternidade excluída com sucesso'
      : 'Atestado excluído com sucesso'
    return confirm({
      title: titleConfirm,
      type: 'danger',
      confirmLabel: 'Excluir',
      onConfirm: () => {
        remove(registro.id)
        alert('success', alertSuccess)
      },
    })()
  }

  const handlePrint = () => {
    const input: AtestadoImpressaoWrapperModel = {
      id: registro.id,
      atestadoModel: atestadoRecente,
      prontuarioId: prontuarioId,
      atendimentoId: atendimentoId,
      atendimentoProfissionalIniciadoEm: registro.atendimentoProfissional.iniciadoEm,
    }
    if (isAssinadoDigitalmente) {
      analytics.logEvent('download_atestado_digital')
    }
    setIsPrintLoading(true)

    const apiPath = registro.recente ? api.atestado.imprimirAtestadoRecente : api.atestado.imprimirAtestado

    const inputAtestado: AtestadoImpressaoInput = {
      id: input.id,
      prontuarioId: input.prontuarioId,
      atendimentoId: input.atendimentoId,
    }

    const inputAtestadoRecente: AtestadoRecenteImpressaoInput = {
      descricao: input.atestadoModel.descricao,
      tipo: input.atestadoModel.tipo,
      prontuarioId: input.prontuarioId,
      atendimentoId: input.atendimentoId,
      atendimentoProfissionalIniciadoEm: input.atendimentoProfissionalIniciadoEm,
    }

    handleImpressaoPath({
      apiPath: apiPath,
      values: registro.recente ? inputAtestadoRecente : inputAtestado,
      file: `Atestado.pdf`,
      dateFormat: reportFormatDate,
    })
      .catch((error) => handleImpressaoError(error, 'Erro ao imprimir atestado.'))
      .finally(() => {
        setIsPrintLoading(false)
      })
  }

  const onSubmitUpdate = (value: AtestadoModel) => {
    update(registro.id, value)
  }

  const EditModal = () => {
    if (atestadoRecente.tipo === TipoAtestadoEnum.EM_BRANCO) {
      return (
        <AtestadoEmBrancoFormModal
          onSubmit={onSubmitUpdate}
          handleModalClose={() => setEditModalOpen(false)}
          cidadaoId={cidadao.id}
          initialValues={atestadoRecente}
          atendimentoId={atendimentoId}
          prontuarioId={prontuarioId}
          dataAtendimento={registro.atendimentoProfissional.iniciadoEm}
        />
      )
    }

    if (atestadoRecente.tipo === TipoAtestadoEnum.LICENCA_MATERNIDADE) {
      return (
        <LicencaMaternidadeFormModal
          onSubmit={onSubmitUpdate}
          handleModalClose={() => setEditModalOpen(false)}
          cidadao={cidadao}
          initialValues={atestadoRecente}
          atendimentoId={atendimentoId}
          prontuarioId={prontuarioId}
          dataAtendimento={registro.atendimentoProfissional.iniciadoEm}
        />
      )
    }

    if (atestadoRecente.tipo === TipoAtestadoEnum.SOLICITACAO_FRALDAS_GERIATRICAS) {
      return (
        <SolicitacaoFraldasGeriatricasFormModal
          onSubmit={onSubmitUpdate}
          handleModalClose={() => setEditModalOpen(false)}
          cidadao={cidadao}
          initialValues={atestadoRecente}
          atendimentoId={atendimentoId}
          prontuarioId={prontuarioId}
          dataAtendimento={registro.atendimentoProfissional.iniciadoEm}
        />
      )
    }

    if (atestadoRecente.tipo === TipoAtestadoEnum.PADRAO) {
      return (
        <AtestadoPadraoFormModal
          onSubmit={onSubmitUpdate}
          handleModalClose={() => setEditModalOpen(false)}
          cidadao={cidadao}
          initialValues={atestadoRecente}
          atendimentoId={atendimentoId}
          prontuarioId={prontuarioId}
          dataAtendimento={registro.atendimentoProfissional.iniciadoEm}
        />
      )
    }
  }

  return (
    <>
      <HFlow justifyContent='flex-end' hSpacing={0} data-testid={dataTestId}>
        <Tooltip text='Visualizar'>
          <Button
            size='small'
            skin='ghost'
            onClick={() => setModalVisualizacaoOpen(true)}
            data-testid='AtestadoButton.Visualizar'
          >
            <Icon icon='zoomOutline' />
          </Button>
        </Tooltip>
        {hasPermissionImprimirAtestado && (
          <Tooltip text='Imprimir'>
            <Button
              size='small'
              skin='ghost'
              onClick={handlePrint}
              loading={isPrintLoading}
              data-testid='AtestadoButton.Imprimir'
            >
              <Icon icon='printerOutline' />
            </Button>
          </Tooltip>
        )}

        {!readOnly && (
          <Tooltip text={tooltipTextMaisOpcoes}>
            <DropdownButton
              disabled={!registro.recente || isEditable || isAssinadoDigitalmente}
              dataTestId='AtestadoButton.Dropdown'
            >
              <DropdownItem
                data-testid='AtestadoButton.Editar'
                onClick={() => {
                  setEditModalOpen(true)
                }}
              >
                Editar
              </DropdownItem>
              <DropdownItem data-testid='AtestadoButton.Excluir' onClick={handleExcluir} type='danger'>
                <HFlow alignItems='center' hSpacing={0.5}>
                  <Icon icon='trashOutline' />
                  Excluir
                </HFlow>
              </DropdownItem>
            </DropdownButton>
          </Tooltip>
        )}
      </HFlow>

      {isModalVisualizacaoOpen && (
        <AtestadosVisualizacaoModal
          registro={registro}
          descricaoAtestadoRecente={atestadoRecente.descricao}
          hasPermissionImprimirAtestado={hasPermissionImprimirAtestado}
          handlePrint={handlePrint}
          handleModalClose={() => setModalVisualizacaoOpen(false)}
          nomeCidadao={cidadao.nome}
          nomeSocialCidadao={cidadao.nomeSocial}
        />
      )}

      {isEditModalOpen && <EditModal />}
    </>
  )
}
